import type { ExportAnnotation } from '@mss_creator/shared';
import type { AnnotationTool } from './viewer-tool';

/**
 * The « Édition » overlay's decision logic — decisions A3/A4/A7 (2026-08-25 doc).
 *
 * Split out from the component because the component's remaining work is
 * genuinely untestable in jsdom (`elementFromPoint`, pointer capture,
 * `getBoundingClientRect` all need real layout) while *these* two steps are
 * where the behaviour actually lives. `readCellHitFromElement` needs only
 * `closest`/`getAttribute`; `resolveAnnotationAction` needs nothing but data.
 */

/** The anchor identity published in the markup (decision A4). */
export interface CellHitIdentity {
  rowKey: string;
  columnNumber: number;
  /** The enclosing tile's scale, needed to store a stroke in unscaled grid pixels. */
  scalePercent: number;
  /** The content line under the pointer, or null when the pointer is over a cell but no line. */
  lineIndex: number | null;
}

export interface CellHit extends CellHitIdentity {
  /** The cell element, so the caller can measure it without a second lookup. */
  cell: Element;
}

/**
 * Resolves the element under the pointer to its anchor identity, or null when
 * it is outside any cell — in which case the gesture is dropped rather than
 * anchored to a guess.
 *
 * Reads the identity from `data-*` rather than inferring it from geometry or
 * matching line text: that inference is precisely the fragility that ruled out
 * annotating the pdf.js canvas (decision A4).
 */
export function readCellHitFromElement(target: Element): CellHit | null {
  const cell = target.closest('[data-row-key]');
  if (cell === null) return null;

  const rowKey = cell.getAttribute('data-row-key');
  const columnNumber = Number(cell.getAttribute('data-column-number'));
  if (rowKey === null || rowKey === '' || Number.isNaN(columnNumber)) return null;

  const page = cell.closest('[data-scale-percent]');
  const parsedScale = Number(page?.getAttribute('data-scale-percent'));
  const scalePercent = Number.isFinite(parsedScale) && parsedScale > 0 ? parsedScale : 100;

  const lineAttribute = target.closest('[data-line-index]')?.getAttribute('data-line-index');
  const parsedLine =
    lineAttribute === undefined || lineAttribute === null ? Number.NaN : Number(lineAttribute);

  return {
    rowKey,
    columnNumber,
    scalePercent,
    lineIndex: Number.isNaN(parsedLine) ? null : parsedLine,
    cell,
  };
}

/**
 * What a click with the armed tool should do. Returns the annotation *without*
 * an id: minting one is the caller's job, which keeps this function pure and
 * therefore assertable.
 */
export type AnnotationAction =
  | {
      type: 'add';
      annotation: { kind: 'highlight'; rowKey: string; columnNumber: number; lineIndex: number };
    }
  | { type: 'remove'; id: string };

export function resolveAnnotationAction(input: {
  tool: AnnotationTool | null;
  hit: CellHitIdentity;
  annotations: readonly ExportAnnotation[];
}): AnnotationAction | null {
  const { tool, hit, annotations } = input;

  if (tool === 'highlight') {
    // A highlight anchors to a line, so a click that missed one has no target.
    if (hit.lineIndex === null) return null;
    const existing = annotations.find(
      (annotation) =>
        annotation.kind === 'highlight' &&
        annotation.rowKey === hit.rowKey &&
        annotation.columnNumber === hit.columnNumber &&
        annotation.lineIndex === hit.lineIndex,
    );
    // Clicking an already-highlighted line clears it, so the tool toggles
    // rather than stacking duplicate highlights on one line.
    if (existing !== undefined) return { type: 'remove', id: existing.id };
    return {
      type: 'add',
      annotation: {
        kind: 'highlight',
        rowKey: hit.rowKey,
        columnNumber: hit.columnNumber,
        lineIndex: hit.lineIndex,
      },
    };
  }

  if (tool === 'erase') {
    const highlightOnLine =
      hit.lineIndex === null
        ? undefined
        : annotations.find(
            (annotation) =>
              annotation.kind === 'highlight' &&
              annotation.rowKey === hit.rowKey &&
              annotation.columnNumber === hit.columnNumber &&
              annotation.lineIndex === hit.lineIndex,
          );
    if (highlightOnLine !== undefined) return { type: 'remove', id: highlightOnLine.id };

    // Strokes are painted inside the iframe and so have no hit area in the
    // overlay; the cell is the finest target available without re-projecting
    // every path purely to hit-test it. "The last thing I drew here" is the
    // only intuitive reading, hence most-recent-first.
    const strokeInCell = [...annotations]
      .reverse()
      .find(
        (annotation) =>
          annotation.kind === 'ink' &&
          annotation.rowKey === hit.rowKey &&
          annotation.columnNumber === hit.columnNumber,
      );
    if (strokeInCell !== undefined) return { type: 'remove', id: strokeInCell.id };
    return null;
  }

  // `ink` commits on pointer-up from the whole trail, which needs geometry this
  // function does not have; `null` means "no tool armed".
  return null;
}
